/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { decorate, observable, runInAction, action } from 'mobx';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Table, Header, Segment, Icon, Label, Popup, Modal, Button, Message } from 'semantic-ui-react';
import { isStoreLoading, isStoreError, isStoreReady } from '@amzn/base-ui/dist/models/BaseStore';
import ErrorBox from '@amzn/base-ui/dist/parts/helpers/ErrorBox';
import ProgressPlaceHolder from '@amzn/base-ui/dist/parts/helpers/BasicProgressPlaceholder';
import { displayError, displaySuccess } from '@amzn/base-ui/dist/helpers/notification';

import { emptyCatalogueCounts } from './catalogueCountUtils';

// This marker in an env type's name is what removes it from the researcher workspace type picker
// -- see the filter in parts/environments-sc/setup/SelectEnvTypeStep.js. The match there is a
// case-sensitive substring test, so the exact spelling and casing matter.
const DEPRECATED_MARKER = 'DEPRECATED';
// Purely cosmetic: sorts retired types after current ones in the admin lists.
const DEPRECATED_NAME_PREFIX = '_';
const DEPRECATED_CATEGORY = 'Deprecated';
const ENV_TYPE_APPROVED = 'approved';

const toDeprecatedName = name => `${DEPRECATED_NAME_PREFIX}${name}-${DEPRECATED_MARKER}`;

// Stands in for a count while scEnvironmentsStore has not finished loading, or when the load
// failed and the list may be only partially populated.
const COUNT_PLACEHOLDER = <span className="catalogue-count-placeholder">&mdash;</span>;

// The `counts` prop default. Kept module level so a detail table mounted without it renders
// zeros instead of throwing, without allocating a fresh value object on every render.
const EMPTY_COUNTS = emptyCatalogueCounts();

// Column widths per view. Description is dropped entirely in compact view, which is what frees
// the horizontal space the half-width card needs.
const COLUMN_WIDTHS = {
  full: { version: '8%', artifactId: '14%', status: '10%', deployed: '8%', terminated: '8%' },
  compact: { version: '12%', artifactId: '20%', status: '14%', deployed: '10%', terminated: '10%' },
};

// expected props
// - detailStore (via prop) — a CatalogueProductDetailStore instance
// - productId (via prop) — the SC product ID
// - compactMode (via prop) — drops the Description column and narrows the fixed-width columns
// - counts (via prop) — a catalogueCountUtils value object, defaults to empty
// - countsReady (via prop) — false while scEnvironmentsStore is loading or errored
// - envTypesStore (via injection)
class CatalogueProductDetail extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      // Snapshot of the upgrade awaiting confirmation, undefined when the modal is closed
      this.pendingUpgrade = undefined;
      this.upgradeProcessing = false;
    });
  }

  get detailStore() {
    return this.props.detailStore;
  }

  handleRetry = () => {
    this.detailStore.load();
  };

  handleImport = (artifactId) => {
    const productId = this.props.productId;
    const candidateId = `${productId}-${artifactId}`;
    this.props.history.push(`/workspace-types-management/import/${encodeURIComponent(candidateId)}`);
  };

  handleUpgradeClick = action((artifactId) => {
    const artifacts = this.detailStore.provisioningArtifacts || [];
    const artifact = _.find(artifacts, a => a.id === artifactId);
    const source = this.getUpgradeSource(artifactId, artifacts);
    const sourceEnvType = source && this.getEnvType(source.id);
    if (!artifact || !source || !sourceEnvType) return;

    // Snapshot rather than deriving in render: the store nodes are mutated as soon as the upgrade
    // runs, which would otherwise leave the open dialog showing the already-renamed values.
    this.pendingUpgrade = {
      artifactId: artifact.id,
      artifactName: artifact.name,
      sourceArtifactId: source.id,
      sourceArtifactName: source.name,
      envTypeId: sourceEnvType.id,
      rev: sourceEnvType.rev,
      name: sourceEnvType.name,
      desc: sourceEnvType.desc,
      category: sourceEnvType.category,
      status: sourceEnvType.status,
    };
  });

  handleCancelUpgrade = action(() => {
    if (this.upgradeProcessing) return;
    this.pendingUpgrade = undefined;
  });

  handleConfirmUpgrade = async () => {
    const previous = this.pendingUpgrade;
    if (!previous) return;

    // Guard against a double click kicking off two creates for the same version
    if (this.upgradeProcessing) return;
    runInAction(() => {
      this.upgradeProcessing = true;
    });

    const productId = this.props.productId;
    const artifact = { id: previous.artifactId, name: previous.artifactName };
    const source = { id: previous.sourceArtifactId, name: previous.sourceArtifactName };
    const envTypesStore = this.props.envTypesStore;
    let created = false;
    try {
      // Create the new version first. If this fails, the previous version is left untouched and
      // still launchable -- deprecating first would leave researchers with no selectable version.
      // params are derived server side and configurations live in S3 keyed by env type id, so
      // neither is carried over: the new version starts with its own params and no configs.
      await envTypesStore.createEnvType({
        id: `${productId}-${artifact.id}`,
        name: previous.name,
        desc: previous.desc,
        category: previous.category,
        status: ENV_TYPE_APPROVED,
        product: { productId },
        provisioningArtifact: { id: artifact.id },
      });
      created = true;

      // Retire the previous version. It stays approved so workspaces already running on it keep
      // working; the DEPRECATED marker in the name is what hides it from new workspace creation.
      await envTypesStore.updateEnvType({
        id: previous.envTypeId,
        rev: previous.rev,
        name: toDeprecatedName(previous.name),
        desc: previous.desc,
        category: DEPRECATED_CATEGORY,
        status: previous.status,
      });

      displaySuccess(
        `Upgraded "${previous.name}" to ${artifact.name}. Version ${source.name} is now deprecated and hidden from new workspaces.`,
      );
    } catch (error) {
      if (created) {
        // Half applied: the new version is live but the old one is still offered to researchers
        displayError(
          `${artifact.name} was imported and approved, but deprecating ${source.name} failed. ` +
            `Edit "${previous.name}" and rename it to "${toDeprecatedName(previous.name)}" to finish.`,
          error,
        );
      } else {
        displayError(error);
      }
    } finally {
      runInAction(() => {
        this.upgradeProcessing = false;
        // Close either way -- on a partial failure the message above explains what to finish by
        // hand, and leaving the dialog open would invite a retry that cannot succeed.
        this.pendingUpgrade = undefined;
      });
    }
  };

  handleEdit = (artifactId) => {
    const productId = this.props.productId;
    const envTypeId = `${productId}-${artifactId}`;
    this.props.history.push(`/workspace-types-management/edit/${encodeURIComponent(envTypeId)}`);
  };

  handleDelete = async (artifactId) => {
    const productId = this.props.productId;
    const envTypeId = `${productId}-${artifactId}`;
    const envTypesStore = this.props.envTypesStore;
    try {
      await envTypesStore.deleteEnvType(envTypeId);
      displaySuccess('Workspace type deleted successfully');
    } catch (error) {
      displayError(error);
    }
  };

  handleTestLaunch = (artifactId) => {
    const productId = this.props.productId;
    const envTypeId = `${productId}-${artifactId}`;
    this.props.history.push(`/workspaces/select-studies?envTypeId=${encodeURIComponent(envTypeId)}`);
  };

  handleApproveRevoke = async (artifactId) => {
    const productId = this.props.productId;
    const envTypeId = `${productId}-${artifactId}`;
    const envTypesStore = this.props.envTypesStore;
    const envType = envTypesStore.getEnvType(envTypeId);
    if (!envType) return;
    try {
      if (envType.isApproved) {
        await envTypesStore.revokeEnvType(envTypeId);
        displaySuccess('Workspace type revoked');
      } else {
        await envTypesStore.approveEnvType(envTypeId);
        displaySuccess('Workspace type approved');
      }
    } catch (error) {
      displayError(error);
    }
  };

  getEnvType(artifactId) {
    const productId = this.props.productId;
    const envTypesStore = this.props.envTypesStore;
    if (!envTypesStore) return null;
    return envTypesStore.getEnvType(`${productId}-${artifactId}`);
  }

  isImported(artifactId) {
    return !!this.getEnvType(artifactId);
  }

  get counts() {
    return this.props.counts || EMPTY_COUNTS;
  }

  // { deployed, terminated } for one version row — either two numbers or two placeholders.
  renderVersionCounts(artifactId) {
    if (!this.props.countsReady) {
      return { deployed: COUNT_PLACEHOLDER, terminated: COUNT_PLACEHOLDER };
    }
    // A version with no imported env type reports zero, regardless of any orphan ScEnvironment
    // records left behind by a deleted env type. Those orphans still show in the card header
    // total, which is the number used to decide whether a product is safe to delete.
    if (!this.isImported(artifactId)) return { deployed: 0, terminated: 0 };
    return this.counts.getVersionCounts(this.props.productId, artifactId);
  }

  // The artifact list arrives sorted newest-first (the backend orders by createdTime desc), so
  // entries after a given artifact are older versions and entries before it are newer ones.
  getOlderArtifacts(artifactId, allArtifacts) {
    const index = _.findIndex(allArtifacts, a => a.id === artifactId);
    return index < 0 ? [] : allArtifacts.slice(index + 1);
  }

  getNewerArtifacts(artifactId, allArtifacts) {
    const index = _.findIndex(allArtifacts, a => a.id === artifactId);
    return index < 0 ? [] : allArtifacts.slice(0, index);
  }

  // The version an upgrade carries Name/Description/Category over from: the most recent older
  // imported version, skipping any that has already been deprecated so that a second upgrade
  // cannot carry a deprecated name forward.
  getUpgradeSource(artifactId, allArtifacts) {
    return _.find(this.getOlderArtifacts(artifactId, allArtifacts), a => {
      const envType = this.getEnvType(a.id);
      return !!envType && !_.includes(envType.name, DEPRECATED_MARKER);
    });
  }

  // Upgrade is offered on every version that is not imported yet, but only enabled when there is
  // an older imported version to upgrade from and no newer version has already been imported.
  getUpgradeState(artifactId, allArtifacts) {
    if (this.isImported(artifactId)) {
      return { enabled: false, reason: 'Already imported' };
    }
    if (_.some(this.getNewerArtifacts(artifactId, allArtifacts), a => this.isImported(a.id))) {
      return { enabled: false, reason: 'A newer version is already imported' };
    }
    const source = this.getUpgradeSource(artifactId, allArtifacts);
    if (!source) {
      return { enabled: false, reason: 'No earlier imported version to upgrade from' };
    }
    return {
      enabled: true,
      source,
      reason: `Upgrade from ${source.name}: import and approve this version, then deprecate ${source.name}`,
    };
  }

  renderArtifactStatus(artifact, allArtifacts) {
    if (!artifact.active) {
      return (
        <Label size="mini" color="grey">
          Archived
        </Label>
      );
    }

    // Find the latest active artifact (first one since they're sorted by creation date desc)
    const latestActive = allArtifacts.find(a => a.active);
    const isLatest = latestActive && latestActive.id === artifact.id;

    if (isLatest) {
      return (
        <Label size="mini" color="green">
          Available
        </Label>
      );
    }

    return (
      <Label size="mini" color="grey">
        Previous
      </Label>
    );
  }

  render() {
    const store = this.detailStore;

    if (isStoreError(store)) {
      return <ErrorBox error={store.error} onRetry={this.handleRetry} />;
    }

    if (isStoreLoading(store)) {
      return <ProgressPlaceHolder segmentCount={1} />;
    }

    if (!isStoreReady(store)) {
      return null;
    }

    return (
      <Segment basic style={{ padding: '8px 0 0 0' }}>
        {this.renderArtifacts()}
        {this.renderUpgradeModal()}
      </Segment>
    );
  }

  renderUpgradeModal() {
    const pending = this.pendingUpgrade;
    if (!pending) return null;

    const artifact = { id: pending.artifactId, name: pending.artifactName };
    const source = { id: pending.sourceArtifactId, name: pending.sourceArtifactName };
    const productName = this.props.productName || this.props.productId;
    const processing = this.upgradeProcessing;
    const currentName = pending.name;
    const currentCategory = pending.category || '(none)';
    const labelStyle = { color: '#6b7280', width: '38%' };
    const beforeStyle = { textDecoration: 'line-through', color: '#9ca3af' };

    return (
      <Modal open onClose={this.handleCancelUpgrade} size="small" closeOnDimmerClick={!processing}>
        <Modal.Header>
          Upgrade to {artifact.name}
          {!processing && (
            <Icon name="close" style={{ float: 'right', cursor: 'pointer' }} onClick={this.handleCancelUpgrade} />
          )}
        </Modal.Header>
        <Modal.Content>
          <Header as="h5" style={{ marginTop: 0 }}>
            {productName}
          </Header>

          <Table definition compact basic="very" size="small">
            <Table.Body>
              <Table.Row>
                <Table.Cell style={labelStyle}>Product</Table.Cell>
                <Table.Cell>
                  {productName} <span style={{ color: '#6b7280' }}>({this.props.productId})</span>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell style={labelStyle}>Upgrading from</Table.Cell>
                <Table.Cell>
                  {source.name} <span style={{ color: '#6b7280' }}>({source.id})</span>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell style={labelStyle}>Upgrading to</Table.Cell>
                <Table.Cell>
                  <strong>{artifact.name}</strong> <span style={{ color: '#6b7280' }}>({artifact.id})</span>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          <Header as="h5">New version ({artifact.name})</Header>
          <Table definition compact basic="very" size="small">
            <Table.Body>
              <Table.Row>
                <Table.Cell style={labelStyle}>Name</Table.Cell>
                <Table.Cell>{currentName}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell style={labelStyle}>Category</Table.Cell>
                <Table.Cell>{currentCategory}</Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell style={labelStyle}>Description</Table.Cell>
                <Table.Cell style={{ color: '#6b7280' }}>
                  Copied from {source.name}
                  {_.isEmpty(pending.desc) ? ' (currently empty)' : ''}
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell style={labelStyle}>Status</Table.Cell>
                <Table.Cell>
                  <Label size="mini" color="green">
                    Approved
                  </Label>{' '}
                  <span style={{ color: '#6b7280' }}>immediately available to researchers</span>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell style={labelStyle}>Configurations</Table.Cell>
                <Table.Cell style={{ color: '#6b7280' }}>Not copied — managed separately in S3</Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          <Header as="h5">Previous version ({source.name})</Header>
          <Table definition compact basic="very" size="small">
            <Table.Body>
              <Table.Row>
                <Table.Cell style={labelStyle}>Name</Table.Cell>
                <Table.Cell>
                  <span style={beforeStyle}>{currentName}</span>
                  <Icon name="arrow right" style={{ margin: '0 6px', color: '#6b7280' }} />
                  <strong>{toDeprecatedName(currentName)}</strong>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell style={labelStyle}>Category</Table.Cell>
                <Table.Cell>
                  <span style={beforeStyle}>{currentCategory}</span>
                  <Icon name="arrow right" style={{ margin: '0 6px', color: '#6b7280' }} />
                  <strong>{DEPRECATED_CATEGORY}</strong>
                </Table.Cell>
              </Table.Row>
              <Table.Row>
                <Table.Cell style={labelStyle}>Status</Table.Cell>
                <Table.Cell>
                  <Label size="mini" color="green">
                    Approved
                  </Label>{' '}
                  <span style={{ color: '#6b7280' }}>left approved so running workspaces keep working</span>
                </Table.Cell>
              </Table.Row>
            </Table.Body>
          </Table>

          <Message info size="small">
            Renaming the previous version to end with {DEPRECATED_MARKER} hides it from new workspace creation, but
            anyone already running a workspace on {source.name} is unaffected. Other imported versions are left
            untouched.
          </Message>
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="grey" disabled={processing} onClick={this.handleCancelUpgrade}>
            Cancel
          </Button>
          <Button primary loading={processing} disabled={processing} onClick={this.handleConfirmUpgrade}>
            Upgrade to {artifact.name}
          </Button>
        </Modal.Actions>
      </Modal>
    );
  }

  renderArtifacts() {
    const artifacts = this.detailStore.provisioningArtifacts || [];
    const compactMode = !!this.props.compactMode;
    const widths = compactMode ? COLUMN_WIDTHS.compact : COLUMN_WIDTHS.full;

    return (
      <div>
        <Header as="h4">
          <Icon name="history" />
          <Header.Content>Product Versions</Header.Content>
        </Header>
        {artifacts.length === 0 ? (
          <div style={{ color: '#6b7280', fontStyle: 'italic' }}>No versions available</div>
        ) : (
          <Table compact basic="very" size="small">
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell style={{ width: widths.version }}>Version</Table.HeaderCell>
                <Table.HeaderCell style={{ width: widths.artifactId }}>Artifact ID</Table.HeaderCell>
                {!compactMode && <Table.HeaderCell>Description</Table.HeaderCell>}
                <Table.HeaderCell style={{ width: widths.status, whiteSpace: 'nowrap' }}>Status</Table.HeaderCell>
                <Table.HeaderCell style={{ width: widths.deployed, whiteSpace: 'nowrap' }} textAlign="center">
                  <Popup
                    content="workspaces on this version that are not terminated"
                    trigger={<span>Deployed</span>}
                    size="mini"
                    inverted
                  />
                </Table.HeaderCell>
                <Table.HeaderCell style={{ width: widths.terminated, whiteSpace: 'nowrap' }} textAlign="center">
                  <Popup
                    content="terminated workspaces on this version"
                    trigger={<span>Terminated</span>}
                    size="mini"
                    inverted
                  />
                </Table.HeaderCell>
                <Table.HeaderCell>Created</Table.HeaderCell>
                <Table.HeaderCell textAlign="center">Actions</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {artifacts.map(artifact => {
                const imported = this.isImported(artifact.id);
                const envType = this.getEnvType(artifact.id);
                const isApproved = envType && envType.isApproved;
                const upgrade = this.getUpgradeState(artifact.id, artifacts);
                const versionCounts = this.renderVersionCounts(artifact.id);
                return (
                  <Table.Row key={artifact.id}>
                    <Table.Cell>{artifact.name}</Table.Cell>
                    <Table.Cell style={{ color: '#6b7280', fontSize: '0.85em' }}>{artifact.id}</Table.Cell>
                    {!compactMode && <Table.Cell>{artifact.description || '-'}</Table.Cell>}
                    <Table.Cell style={{ whiteSpace: 'nowrap' }}>
                      {imported ? (
                        <Label size="mini" color={isApproved ? 'green' : 'orange'}>
                          {isApproved ? 'Approved' : 'Not Approved'}
                        </Label>
                      ) : (
                        this.renderArtifactStatus(artifact, artifacts)
                      )}
                    </Table.Cell>
                    <Table.Cell textAlign="center">{versionCounts.deployed}</Table.Cell>
                    <Table.Cell textAlign="center">{versionCounts.terminated}</Table.Cell>
                    <Table.Cell>{artifact.createdTime || '-'}</Table.Cell>
                    <Table.Cell textAlign="center">
                      <span style={{ display: 'inline-flex', gap: '8px' }}>
                        <Popup
                          content={imported ? 'Already imported' : 'Import'}
                          trigger={
                            <Icon
                              name="download"
                              style={{ cursor: imported ? 'default' : 'pointer', color: imported ? '#d0d0d0' : '#2185d0' }}
                              onClick={imported ? undefined : () => this.handleImport(artifact.id)}
                            />
                          }
                          size="mini"
                          inverted
                        />
                        {!imported && (
                          <Popup
                            content={upgrade.reason}
                            trigger={
                              <Icon
                                name="level up alternate"
                                style={{
                                  cursor: upgrade.enabled ? 'pointer' : 'default',
                                  color: upgrade.enabled ? '#00b5ad' : '#d0d0d0',
                                }}
                                onClick={upgrade.enabled ? () => this.handleUpgradeClick(artifact.id) : undefined}
                              />
                            }
                            size="mini"
                            inverted
                          />
                        )}
                        {imported && (
                          <Popup
                            content={isApproved ? 'Revoke' : 'Approve'}
                            trigger={
                              <Icon
                                name={isApproved ? 'eye slash' : 'eye'}
                                style={{ cursor: 'pointer', color: isApproved ? '#f2711c' : '#21ba45' }}
                                onClick={() => this.handleApproveRevoke(artifact.id)}
                              />
                            }
                            size="mini"
                            inverted
                          />
                        )}
                        {imported && !isApproved && (
                          <Popup
                            content="Test launch"
                            trigger={
                              <Icon
                                name="play"
                                style={{ cursor: 'pointer', color: '#6435c9' }}
                                onClick={() => this.handleTestLaunch(artifact.id)}
                              />
                            }
                            size="mini"
                            inverted
                          />
                        )}
                        {imported && (
                          <Popup
                            content="Edit"
                            trigger={
                              <Icon
                                name="pencil"
                                style={{ cursor: 'pointer', color: '#2185d0' }}
                                onClick={() => this.handleEdit(artifact.id)}
                              />
                            }
                            size="mini"
                            inverted
                          />
                        )}
                        {imported && (
                          <Popup
                            content={isApproved ? 'Revoke before deleting' : 'Delete'}
                            trigger={
                              <Icon
                                name="trash"
                                style={{ cursor: isApproved ? 'default' : 'pointer', color: isApproved ? '#d0d0d0' : '#db2828' }}
                                onClick={isApproved ? undefined : () => this.handleDelete(artifact.id)}
                              />
                            }
                            size="mini"
                            inverted
                          />
                        )}
                      </span>
                    </Table.Cell>
                  </Table.Row>
                );
              })}
            </Table.Body>
          </Table>
        )}
      </div>
    );
  }
}

decorate(CatalogueProductDetail, {
  pendingUpgrade: observable,
  upgradeProcessing: observable,
});

export default inject('envTypesStore')(withRouter(observer(CatalogueProductDetail)));
